"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import useCapacitor from "@/hooks/useCapacitor";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import UserPageTab from "./UserPageTab";
import { DEFAULT_USER_PAGE_TAB, USER_PAGE_TABS, type UserPageTabConfig, type UserPageTabKey, type UserPageVisibilityContext, getUserPageTabByRoute } from "./userTabs.config";
const DEFAULT_TAB = DEFAULT_USER_PAGE_TAB;

// Normalize consent country to uppercase code; empty or non-strings become null.
const normalizeCountry = (country: string | null | undefined): string | null => {
  if (typeof country !== "string") {
    return null;
  }
  const trimmed = country.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};
const getVisibilityContext = ({
  showWaves,
  capacitorIsIos,
  country,
  isOwnProfile
}: {
  readonly showWaves: boolean;
  readonly capacitorIsIos: boolean;
  readonly country: string | null | undefined;
  readonly isOwnProfile: boolean;
}): UserPageVisibilityContext => {
  const normalizedCountry = normalizeCountry(country);
  return {
    showWaves,
    hideSubscriptions: capacitorIsIos && normalizedCountry !== "US",
    isOwnProfile
  };
};
const resolveTabFromPath = (pathname: string): UserPageTabKey => {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] ?? "";
  const match = getUserPageTabByRoute(routeSegment);
  return match?.id ?? DEFAULT_TAB;
};
const filterVisibleTabs = (tabs: readonly UserPageTabConfig[], context: UserPageVisibilityContext) => tabs.filter(tab => tab.isVisible ? tab.isVisible(context) : true);
export default function UserPageTabs() {
  const $ = _c(60);
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const params = useParams();
  let t0;
  if ($[0] !== params?.user) {
    t0 = params?.user?.toString() ?? "";
    $[0] = params?.user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleOrWallet = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams?.toString() ?? "";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchString = t1;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves,
    connectedProfile
  } = useContext(AuthContext);
  let t2;
  bb0: {
    if (!connectedProfile || !handleOrWallet) {
      t2 = false;
      break bb0;
    }
    let t3;
    if ($[4] !== handleOrWallet) {
      t3 = handleOrWallet.toLowerCase();
      $[4] = handleOrWallet;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const lower = t3;
    if (connectedProfile.normalised_handle === lower) {
      t2 = true;
      break bb0;
    }
    let t4;
    if ($[6] !== connectedProfile.wallets || $[7] !== lower) {
      t4 = connectedProfile.wallets?.some(w => w.wallet.toLowerCase() === lower) ?? false;
      $[6] = connectedProfile.wallets;
      $[7] = lower;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t2 = t4;
  }
  const isOwnProfile = t2;
  let t3;
  if ($[9] !== capacitor.isIos || $[10] !== country || $[11] !== isOwnProfile || $[12] !== showWaves) {
    t3 = getVisibilityContext({
      showWaves,
      capacitorIsIos: capacitor.isIos,
      country,
      isOwnProfile
    });
    $[9] = capacitor.isIos;
    $[10] = country;
    $[11] = isOwnProfile;
    $[12] = showWaves;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const visibilityContext = t3;
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t4;
  if ($[14] !== pathname) {
    t4 = resolveTabFromPath(pathname);
    $[14] = pathname;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const resolvedTabFromPath = t4;
  let t5;
  if ($[16] !== visibilityContext) {
    t5 = filterVisibleTabs(USER_PAGE_TABS, visibilityContext);
    $[16] = visibilityContext;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const visibleTabs = t5;
  let t6;
  if ($[18] !== resolvedTabFromPath || $[19] !== visibleTabs) {
    let t7;
    if ($[21] !== resolvedTabFromPath) {
      t7 = tab => tab.id === resolvedTabFromPath;
      $[21] = resolvedTabFromPath;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    t6 = visibleTabs.some(t7);
    $[18] = resolvedTabFromPath;
    $[19] = visibleTabs;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const resolvedTabIsVisible = t6;
  let t7;
  bb1: {
    if (resolvedTabIsVisible) {
      t7 = resolvedTabFromPath;
      break bb1;
    }
    const firstVisibleTab = visibleTabs[0]?.id;
    t7 = firstVisibleTab ?? DEFAULT_TAB;
  }
  const activeTab = t7;
  let t8;
  if ($[23] !== handleOrWallet || $[24] !== pathname || $[25] !== resolvedTabIsVisible || $[26] !== router || $[27] !== searchString || $[28] !== visibleTabs[0] || $[29] !== visibleTabs.length) {
    t8 = () => {
      if (!visibleTabs.length || resolvedTabIsVisible || !handleOrWallet) {
        return;
      }
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab) {
        return;
      }
      const fallbackPath = fallbackTab.route ? `/${handleOrWallet}/${fallbackTab.route}` : `/${handleOrWallet}`;
      if (fallbackPath === pathname) {
        return;
      }
      const nextUrl = searchString ? `${fallbackPath}?${searchString}` : fallbackPath;
      router.replace(nextUrl);
    };
    $[23] = handleOrWallet;
    $[24] = pathname;
    $[25] = resolvedTabIsVisible;
    $[26] = router;
    $[27] = searchString;
    $[28] = visibleTabs[0];
    $[29] = visibleTabs.length;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== handleOrWallet || $[32] !== pathname || $[33] !== resolvedTabIsVisible || $[34] !== router || $[35] !== searchString || $[36] !== visibleTabs) {
    t9 = [handleOrWallet, pathname, resolvedTabIsVisible, router, searchString, visibleTabs];
    $[31] = handleOrWallet;
    $[32] = pathname;
    $[33] = resolvedTabIsVisible;
    $[34] = router;
    $[35] = searchString;
    $[36] = visibleTabs;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  useEffect(t8, t9);
  let t10;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const checkScroll = t10;
  let t11;
  let t12;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(container_0);
        if (contentContainer) {
          resizeObserver.observe(contentContainer);
        }
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver?.disconnect();
      };
    };
    t12 = [checkScroll];
    $[39] = t11;
    $[40] = t12;
  } else {
    t11 = $[39];
    t12 = $[40];
  }
  useEffect(t11, t12);
  let t13;
  if ($[41] !== visibleTabs.length) {
    t13 = () => {
      if (!visibleTabs.length) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[41] = visibleTabs.length;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== visibleTabs) {
    t14 = [checkScroll, visibleTabs];
    $[43] = visibleTabs;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  useEffect(t13, t14);
  let t15;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      container_1.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const scrollLeft_0 = t15;
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const scrollRight = t16;
  let t17;
  if ($[47] !== activeTab || $[48] !== visibleTabs) {
    t17 = visibleTabs.map(tabConfig => <UserPageTab key={tabConfig.id} tab={tabConfig} activeTabId={activeTab} parentRef={scrollContainerRef} />);
    $[47] = activeTab;
    $[48] = visibleTabs;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t17) {
    t18 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4" aria-label="Tabs">{t17}</div></div>;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== canScrollLeft) {
    t19 = canScrollLeft && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-group tw-absolute tw-left-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-start tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[52] = canScrollLeft;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== canScrollRight) {
    t20 = canScrollRight && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-group tw-absolute tw-right-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-end tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[54] = canScrollRight;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t18 || $[57] !== t19 || $[58] !== t20) {
    t21 = <div className="tw-relative tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700">{t18}{t19}{t20}</div>;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  return t21;
}